import { useQuery } from '@tanstack/react-query';
import client from '@/api/client';
import type { Hostel } from '@/api/types';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hostelStatusLabel } from '@/lib/labels';

const STATUS_COLORS = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  ACTIVE:           'bg-green-100 text-green-800',
  REJECTED:         'bg-red-100 text-red-700',
  SUSPENDED:        'bg-gray-100 text-gray-700',
};

export function SupervisorHostelsPage() {
  const { t } = useTranslation();
  const { data: hostels = [], isLoading } = useQuery<Hostel[]>({
    queryKey: ['supervisor-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('hostel.title')}</h1>
      </div>

      {isLoading ? (
        <p className="text-gray-400">{t('common.loading')}</p>
      ) : hostels.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-dashed border-gray-200">
          <Building2 size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-600 mb-1">{t('dashboard.noHostelsAssigned')}</p>
          <p className="text-sm text-gray-400">{t('dashboard.contactOwner')}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {hostels.map(h => (
            <div key={h.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-gray-900">{h.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[h.status]}`}>
                    {hostelStatusLabel(t, h.status)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{h.address}, {h.city}, {h.state} {h.pincode}</p>
              </div>
              {h.status === 'ACTIVE' && (
                <Link
                  to="/supervisor/hostels/$id"
                  params={{ id: h.id }}
                  search={{ bedStatus: '' }}
                  className="ml-4 flex items-center gap-1 text-violet-600 text-sm font-medium hover:underline"
                >
                  {t('common.manage')} <ChevronRight size={14} /></Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
